import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/ui/cn';

const STRENGTH_KEYS = [
  'account.strengthTooWeak',
  'account.strengthWeak',
  'account.strengthFair',
  'account.strengthGood',
  'account.strengthStrong',
] as const;

/** Client-side strength heuristic only; the server (Argon2id) is authoritative. */
export function scorePassword(pw: string): { score: 0 | 1 | 2 | 3 | 4 } {
  let score = 0;
  if (pw.length >= 10) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return { score: score as 0 | 1 | 2 | 3 | 4 };
}

export function PasswordStrength({ value }: { value: string }) {
  const { t } = useTranslation();
  const { score } = useMemo(() => scorePassword(value), [value]);
  if (!value) return null;
  const label = t(STRENGTH_KEYS[score]);
  const tone = score >= 3 ? 'bg-signal-teal' : score === 2 ? 'bg-signal-amber' : 'bg-signal-error';
  return (
    <div className="flex items-center gap-sm">
      <div className="flex h-[6px] flex-1 gap-[3px]">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn('flex-1 rounded-pill', i < score ? tone : 'bg-hairline-strong')}
          />
        ))}
      </div>
      <span className="font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">{label}</span>
    </div>
  );
}
